import { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import PaymentFallbackModal from '../../components/checkout/PaymentFallbackModal';
import PhoneVerificationModal from '../../components/checkout/PhoneVerificationModal';
import {
  ShoppingCart, Trash2, Loader2, ArrowRight, ArrowLeft,
  Package, Truck, Building, Globe, GraduationCap,
  FlaskConical, Code, Bot, Cog, Calculator, Tag, X, Check,
  FileText, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { trackBeginCheckout, trackRemoveFromCart } from '../../utils/ga4';

// Category icon mapping
const CATEGORY_ICONS = {
  science: FlaskConical,
  coding: Code,
  technology: Code,
  robotics: Bot,
  engineering: Cog,
  math: Calculator,
};

const CATEGORY_COLORS = {
  science: 'from-rose-500 to-pink-400',
  coding: 'from-blue-500 to-indigo-400',
  technology: 'from-blue-500 to-indigo-400',
  robotics: 'from-orange-500 to-amber-400',
  engineering: 'from-emerald-500 to-teal-400',
  math: 'from-purple-500 to-violet-400',
};

export default function CartPage() {
  const navigate = useNavigate();
  const { cart, removeFromCart, removeCampFromCart, clearCart, checkout, loading, hasShippableItems, refreshCart } = useCart();

  // Re-sync cart from server on page load — ensures guest session cart is current
  // (session cookie may have been set by add-to-cart after the context's initial fetch).
  useEffect(() => {
    refreshCart();
  }, [refreshCart]);
  const { user, api } = useAuth();
  const [checkingOut, setCheckingOut] = useState(false);
  const [showFallbackModal, setShowFallbackModal] = useState(false);
  const [showPhoneVerifyModal, setShowPhoneVerifyModal] = useState(false);

  // Cart-level coupon state (Feb 2026 — moved here from the inline panel).
  // We hit /api/cart/apply-coupon/ which re-prices every camp line and
  // returns a single discount summary so the parent sees one explicit
  // "Discount (CODE) -$X" row in the Order Summary.
  const [couponCode, setCouponCode] = useState('');
  const [couponApplying, setCouponApplying] = useState(false);
  const appliedCoupon = useMemo(() => {
    const items = (cart.items || []).filter(i => i.coupon_code);
    if (!items.length) return null;
    const code = items[0].coupon_code;
    const label = items[0].discount_label || `${code}`;
    const discount_amount = items.reduce((s, i) => s + Number(i.discount_amount || 0), 0);
    return { code, label, discount_amount };
  }, [cart.items]);

  const handleApplyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) { toast.error('Enter a coupon code'); return; }
    setCouponApplying(true);
    try {
      await api.post('/cart/apply-coupon', { code });
      await refreshCart();
      toast.success('Coupon applied!');
      setCouponCode('');
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.error_message ||
        'Invalid coupon code';
      toast.error(msg);
    } finally {
      setCouponApplying(false);
    }
  };

  const handleRemoveCoupon = async () => {
    setCouponApplying(true);
    try {
      await api.delete('/cart/coupon');
      await refreshCart();
      setCouponCode('');
      toast.success('Coupon removed');
    } catch (err) {
      toast.error('Failed to remove coupon');
    } finally {
      setCouponApplying(false);
    }
  };
  const [shippingAddress, setShippingAddress] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: ''
  });

  const [guestInfo, setGuestInfo] = useState({
    parentFirstName: '', parentLastName: '', parentEmail: '', parentPhone: '',
    studentFirstName: '', studentLastName: '', studentGrade: '',
  });
  const setGuestField = (field, value) => setGuestInfo(prev => ({ ...prev, [field]: value }));

  // Calculate if we need shipping form
  const needsShipping = useMemo(() => {
    return cart.items?.some(item => 
      item.include_kit && item.kit_delivery_method === 'ship'
    ) || false;
  }, [cart.items]);

  const handleRemoveItem = async (sectionId, learnerId) => {
    const item = (cart.items || []).find(
      it => (it.section_id === sectionId || it.id === sectionId) && (!learnerId || it.learner_id === learnerId),
    );
    try {
      await removeFromCart(sectionId, learnerId);
      if (item) {
        trackRemoveFromCart({
          id: item.section_id || item.id,
          name: item.section_title || item.name,
          category: item.category || 'After-School Program',
          location: item.section_location || item.location_name || null,
          price: Number(item.total || 0),
        });
      }
    } catch (error) {
      // Error handled in context
    }
  };

  const handleRemoveCampItem = async (campSectionId, learnerId, guestStudentName) => {
    const item = (cart.items || []).find(
      it => (it.camp_section_id === campSectionId || it.id === campSectionId) && (!learnerId || it.learner_id === learnerId),
    );
    try {
      await removeCampFromCart(campSectionId, learnerId, guestStudentName);
      if (item) {
        trackRemoveFromCart({
          id: item.camp_id || item.camp_section_id || item.id,
          name: item.camp_name || item.name,
          category: 'summer_camp',
          location: item.location_name || null,
          price: Number(item.total || item.camp_price || 0),
        });
      }
    } catch (error) {
      // Error handled in context
    }
  };

  const handleShippingChange = (field, value) => {
    setShippingAddress(prev => ({ ...prev, [field]: value }));
  };

  const isShippingValid = () => {
    if (!needsShipping) return true;
    return shippingAddress.name && 
           shippingAddress.address && 
           shippingAddress.city && 
           shippingAddress.state && 
           shippingAddress.zip;
  };

  const isGuestInfoValid = () => {
    const { parentFirstName, parentLastName, parentEmail, parentPhone, studentFirstName, studentLastName, studentGrade } = guestInfo;
    const phoneDigits = (parentPhone || '').replace(/\D/g, '');
    return parentFirstName && parentLastName && parentEmail &&
           /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(parentEmail) &&
           phoneDigits.length >= 10 &&
           studentFirstName && studentLastName && studentGrade;
  };

  const executeCheckout = async () => {
    // GA4 Ecommerce — begin_checkout (fire BEFORE Stripe redirect).
    // Use the camp/section identifier as item_id and pass quantity from the
    // cart line so revenue equals price × quantity per GA4 spec.
    trackBeginCheckout(
      (cart.items || []).map(it => {
        const isCamp = it.type === 'camp' || Boolean(it.camp_id);
        const qty = Number(
          it.quantity ?? it.session_summary?.total ?? (it.selected_days?.length || 1),
        ) || 1;
        const price = Number(it.camp_price ?? it.total ?? it.price ?? 0);
        return {
          id: it.camp_id || it.section_id || it.camp_section_id || it.id,
          name: it.camp_name || it.section_title || it.name || 'Camp',
          category: isCamp ? 'summer_camp' : (it.category || 'After-School Program'),
          location: it.location_name || it.section_location || null,
          grade_band: it.learner_grade || it.grade_band,
          // Send a per-unit price so GA4 value = price × quantity matches the
          // line total. When backend only exposes a line total, fall back to
          // total / quantity to keep the math honest.
          price: qty > 0 ? Number((price / qty).toFixed(2)) : price,
          quantity: qty,
        };
      }),
    );

    setCheckingOut(true);
    try {
      const guestPayload = !user ? {
        guest_parent_name:   `${guestInfo.parentFirstName} ${guestInfo.parentLastName}`.trim(),
        guest_parent_email:  guestInfo.parentEmail,
        guest_parent_phone:  guestInfo.parentPhone || '',
        guest_student_name:  `${guestInfo.studentFirstName} ${guestInfo.studentLastName}`.trim(),
        guest_student_grade: guestInfo.studentGrade,
      } : null;
      const { url } = await checkout(needsShipping ? shippingAddress : null, guestPayload);
      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      if (error.response?.data?.error_code === 'KIT_OUT_OF_STOCK') {
        toast.error(error.response.data.detail, { duration: 6000 });
      } else if (error.response?.data?.error_code === 'DOCUMENTS_REQUIRED') {
        const docs = error.response.data.unsigned_documents || [];
        setUnsignedDocs(docs);
        toast.error('Please sign the required agreement documents before completing your purchase.', { duration: 6000 });
        document.getElementById('documents-required-banner')?.scrollIntoView({ behavior: 'smooth' });
      } else {
        toast.error('Payment processing unavailable. Please choose an alternative payment method.');
        setShowFallbackModal(true);
      }
    } finally {
      setCheckingOut(false);
    }
  };

  const handleCheckout = async () => {
    if (!user && !isGuestInfoValid()) {
      // Find the FIRST missing/invalid field in declaration order so we can
      // toast a clear "fix these" message AND focus the user on the first
      // field they need to correct (parent/student form starts with First Name,
      // not Phone — the previous flow was confusing because it surfaced a
      // phone-specific message even when other fields were also empty).
      const fieldOrder = [
        { key: 'parentFirstName',  testid: 'guest-parent-first'  },
        { key: 'parentLastName',   testid: 'guest-parent-last'   },
        { key: 'parentEmail',      testid: 'guest-parent-email',
          isInvalid: v => !v || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) },
        { key: 'parentPhone',      testid: 'guest-parent-phone',
          isInvalid: v => (v || '').replace(/\D/g, '').length < 10 },
        { key: 'studentFirstName', testid: 'guest-student-first' },
        { key: 'studentLastName',  testid: 'guest-student-last'  },
        { key: 'studentGrade',     testid: 'guest-student-grade' },
      ];
      const firstMissing = fieldOrder.find(f =>
        f.isInvalid ? f.isInvalid(guestInfo[f.key]) : !guestInfo[f.key]
      );
      toast.error('Please enter the required fields in Your Information');
      document.getElementById('guest-info-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (firstMissing) {
        setTimeout(() => {
          const el = document.querySelector(`[data-testid="${firstMissing.testid}"]`);
          if (el) {
            // For native <input> use focus(); for the shadcn <Select> trigger use click()
            if (typeof el.focus === 'function') el.focus();
            // Some Select triggers need click to open — but only when no value yet.
            if (firstMissing.key === 'studentGrade' && el.tagName === 'BUTTON') el.click();
          }
        }, 400);
      }
      return;
    }

    if (needsShipping && !isShippingValid()) {
      toast.error('Please complete the shipping address');
      return;
    }

    // Guest users must verify their phone before payment
    if (!user) {
      setShowPhoneVerifyModal(true);
      return;
    }

    await executeCheckout();
  };

  const handleFreeCheckout = async () => {
    setCheckingOut(true);
    try {
      await api.post('/cart/free-checkout');
      await refreshCart();
      navigate('/checkout/success', {
        state: { free_checkout: true }
      });
    } catch (err) {
      if (err.response?.data?.error_code === 'DOCUMENTS_REQUIRED') {
        const docs = err.response.data.unsigned_documents || [];
        setUnsignedDocs(docs);
        toast.error('Please sign the required agreement documents before completing your registration.', { duration: 6000 });
        document.getElementById('documents-required-banner')?.scrollIntoView({ behavior: 'smooth' });
      } else {
        toast.error(err.response?.data?.detail || 'Enrollment failed. Please try again.');
      }
    } finally {
      setCheckingOut(false);
    }
  };

  const handleFallbackSuccess = (data) => {
    setShowFallbackModal(false);
    clearCart();
    // Navigate to success page with confirmation details
    navigate('/checkout/success', {
      state: {
        alternative_payment: true,
        confirmation_number: data.confirmation_number,
        payment_method: data.payment_method,
        instructions: data.instructions,
        total_amount: data.total_amount
      }
    });
  };

  // Agreement document gate
  const [unsignedDocs, setUnsignedDocs] = useState([]);
  const [docsChecked, setDocsChecked] = useState(false);

  useEffect(() => {
    if (!user || !cart.items?.length) { setUnsignedDocs([]); setDocsChecked(true); return; }
    api.get('/documents/cart-check/')
      .then(r => { setUnsignedDocs(r.data.unsigned || []); })
      .catch(() => {})
      .finally(() => setDocsChecked(true));
  }, [user, cart.items]);

  const isEmpty = !cart.items || cart.items.length === 0;

  // Calculate course and kit totals separately
  const totals = useMemo(() => {
    let courseTotal = 0;
    let kitTotal = 0;
    let extendedCareTotal = 0;
    let campCount = 0;
    let courseCount = 0;
    cart.items?.forEach(item => {
      if (item.type === 'camp') {
        courseTotal += Number(item.camp_price || 0);
        kitTotal += Number(item.kit_total || 0);
        extendedCareTotal += Number(item.extended_care_cost || 0);
        campCount++;
      } else {
        courseTotal += Number(item.section_price) || Number(item.price) || 0;
        if (item.include_kit && item.kit) {
          kitTotal += Number(item.kit.price) || 0;
        }
        courseCount++;
      }
    });
    return { courseTotal, kitTotal, extendedCareTotal, grandTotal: courseTotal + kitTotal + extendedCareTotal, campCount, courseCount };
  }, [cart.items]);

  return (
    <div className="section-padding">
      <div className="container-app max-w-4xl">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-heading text-3xl font-bold">Shopping Cart</h1>
          {!isEmpty && (
            <Button variant="ghost" onClick={clearCart} disabled={loading} data-testid="clear-cart-btn">
              <Trash2 className="h-4 w-4 mr-2" />
              Clear Cart
            </Button>
          )}
        </div>

        {isEmpty ? (
          <Card className="card-dashboard">
            <CardContent className="py-16 text-center">
              <ShoppingCart className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Your cart is empty</h2>
              <p className="text-muted-foreground mb-6">
                Browse our catalogue to find programmes and add them to your cart.
              </p>
              <Link to="/catalog">
                <Button className="btn-primary" data-testid="browse-courses-btn">
                  Browse Catalogue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left — Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {/* Document signing banner */}
              {user && docsChecked && unsignedDocs.length > 0 && (
                <div id="documents-required-banner" className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-amber-900 dark:text-amber-100">Agreement Documents Required</p>
                      <p className="text-sm text-amber-700 dark:text-amber-300 mt-0.5">
                        Please sign the following documents before completing your purchase.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {unsignedDocs.map((d, i) => (
                      <div key={i} className="flex items-center justify-between bg-white dark:bg-amber-900/20 rounded-md px-3 py-2 text-sm">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-amber-600" />
                          <span className="font-medium">{d.document_title}</span>
                          {d.learner_name && <span className="text-muted-foreground">· for {d.learner_name}</span>}
                        </div>
                        <a href={(() => {
                            const p = new URLSearchParams();
                            if (d.learner_id) p.set('learner', d.learner_id);
                            if (d.section_id) p.set('section_id', d.section_id);
                            if (d.camp_section_id) p.set('camp_section_id', d.camp_section_id);
                            const qs = p.toString();
                            return `/parent/documents/${d.document_id}/sign${qs ? `?${qs}` : ''}`;
                          })()}>
                          <Button size="sm" variant="default" className="h-7 text-xs">
                            Sign Now
                          </Button>
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* TOP Checkout Button - Quick Access */}
              <div className="flex items-center justify-between gap-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div>
                  <p className="font-semibold text-lg">Ready to checkout?</p>
                  <p className="text-sm text-muted-foreground">Review items below or proceed now</p>
                </div>
                <Button 
                  onClick={handleCheckout}
                  disabled={loading}
                  size="lg"
                  className="flex-shrink-0 h-11 min-h-[44px]"
                  data-testid="checkout-top-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Checkout Now
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>

              {cart.items.map((item, index) => {
                const isCamp = item.type === 'camp';
                const isAfterSchool = item.type === 'after_school';
                return (
                <Card key={isCamp ? `camp-${item.camp_section_id}-${item.learner_id}` : isAfterSchool ? `as-${item.course_title}-${item.learner_id}` : `${item.section_id}-${item.learner_id}`} className="card-dashboard" data-testid={`cart-item-${index}`}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{isCamp ? item.camp_name : item.course_title}</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          For: {item.learner_name}
                          {item.is_guest && <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Guest</span>}
                          {item.learner_grade && <span className="text-xs ml-1">(Grade {item.learner_grade})</span>}
                        </p>
                        {item.is_guest && (
                          <p className="text-xs text-muted-foreground mt-1">
                            📧 Parent: {item.guest_parent_name} · {item.guest_parent_email}
                          </p>
                        )}

                        {/* After-School info */}
                        {isAfterSchool && (
                          <div className="mt-2 space-y-1">
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                              After-School · {item.day}s
                            </Badge>
                            <p className="text-xs text-muted-foreground">{item.location} · {item.time}</p>
                            <p className="text-xs text-muted-foreground">{item.dates}</p>
                          </div>
                        )}

                        {/* Camp-specific info */}
                        {isCamp && (
                          <div className="mt-2 space-y-1">
                            {/* Session Summary */}
                            {item.session_summary ? (
                              <div className="flex flex-wrap gap-2">
                                {item.session_summary.full_day > 0 && (
                                  <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                                    {item.session_summary.full_day} Full Day{item.session_summary.full_day !== 1 ? 's' : ''}
                                  </Badge>
                                )}
                                {item.session_summary.am > 0 && (
                                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                    {item.session_summary.am} Morning{item.session_summary.am !== 1 ? 's' : ''}
                                  </Badge>
                                )}
                                {item.session_summary.pm > 0 && (
                                  <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">
                                    {item.session_summary.pm} Afternoon{item.session_summary.pm !== 1 ? 's' : ''}
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">
                                {item.is_full_camp ? `Full Camp (${item.selected_days.length} days)` : `${item.selected_days.length} day${item.selected_days.length !== 1 ? 's' : ''}: ${item.selected_days.map(d => `Day ${d}`).join(', ')}`}
                              </Badge>
                            )}
                            
                            {/* Day-by-day breakdown */}
                            {item.selected_sessions && item.selected_sessions.length > 0 && (
                              <div className="mt-2 p-2 bg-gray-50 rounded text-xs space-y-0.5">
                                {item.selected_sessions.sort((a, b) => a.day - b.day).map(s => {
                                  // Calculate the actual date for this day
                                  let dateDisplay = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'][s.day - 1];
                                  if (item.start_date) {
                                    const [year, month, day] = item.start_date.split('-').map(Number);
                                    const startDate = new Date(year, month - 1, day);
                                    const currentDate = new Date(startDate);
                                    currentDate.setDate(startDate.getDate() + (s.day - 1));
                                    
                                    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                    
                                    const dayName = dayNames[currentDate.getDay()];
                                    const monthName = monthNames[currentDate.getMonth()];
                                    const dayOfMonth = currentDate.getDate();
                                    
                                    dateDisplay = `${dayName}, ${monthName} ${dayOfMonth}`;
                                  }
                                  
                                  return (
                                    <div key={s.day} className="text-gray-700">
                                      <div>
                                        • {dateDisplay}: {
                                          s.session === 'am' ? '9 AM - 12 PM' : 
                                          s.session === 'pm' ? '1 PM - 4 PM' : 
                                          '9 AM - 4 PM'
                                        }
                                      </div>
                                      {s.course_title && (
                                        s.course_activities?.length ? (
                                          <TooltipProvider delayDuration={120}>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <button
                                                  type="button"
                                                  className="ml-2 text-primary font-medium underline decoration-dotted underline-offset-2 cursor-help text-left"
                                                  data-testid={`cart-course-day-${s.day}`}
                                                >
                                                  {s.course_title}
                                                </button>
                                              </TooltipTrigger>
                                              <TooltipContent
                                                side="top"
                                                className="max-w-xs bg-white text-foreground border border-border shadow-lg p-3"
                                                data-testid={`cart-course-tooltip-${s.day}`}
                                              >
                                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                                                  What they'll do
                                                </p>
                                                <ul className="space-y-1 text-xs text-foreground">
                                                  {s.course_activities.map((a, i) => (
                                                    <li key={i} className="flex gap-1.5">
                                                      <span className="text-primary">•</span>
                                                      <span>{a}</span>
                                                    </li>
                                                  ))}
                                                </ul>
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        ) : (
                                          <div className="ml-2 text-primary font-medium" data-testid={`cart-course-day-${s.day}`}>
                                            {s.course_title}
                                          </div>
                                        )
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            
                            <p className="text-xs text-muted-foreground">{item.location_name}{item.start_date ? ` · ${item.start_date} — ${item.end_date}` : ''}</p>
                            {item.kits?.length > 0 && (
                              <div className="mt-2 p-2 bg-primary/5 rounded-lg border border-primary/20">
                                <p className="text-xs font-medium flex items-center gap-1"><Package className="h-3 w-3 text-primary" /> STEM Kits Included:</p>
                                {item.kits.map((k, ki) => (
                                  <p key={ki} className="text-xs text-muted-foreground ml-5">Day {k.day}: {k.name} (+${k.price.toFixed(2)})</p>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Course delivery type badge */}
                        {!isCamp && item.delivery_type && (
                          <Badge variant="outline" className="mt-2 text-xs">
                            {item.delivery_type === 'online' ? (
                              <><Globe className="h-3 w-3 mr-1" /> Online</>
                            ) : (
                              <><Building className="h-3 w-3 mr-1" /> In-Person</>
                            )}
                          </Badge>
                        )}
                        
                        {/* Kit info for course items */}
                        {!isCamp && item.include_kit && item.kit && (
                          <div className="mt-3 p-3 bg-primary/5 rounded-lg border border-primary/20">
                            <div className="flex items-center gap-2">
                              <Package className="h-4 w-4 text-primary" />
                              <span className="font-medium text-sm">{item.kit.name}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                              {item.kit_delivery_method === 'ship' ? (
                                <><Truck className="h-3 w-3" /> Shipping to your address</>
                              ) : (
                                <><Building className="h-3 w-3" /> Pickup at class location</>
                              )}
                            </div>
                            <p className="text-sm font-medium text-primary mt-1">
                              +${(Number(item.kit.price) || 0).toFixed(2)}
                            </p>
                          </div>
                        )}
                      </div>
                      
                      <div className="text-right ml-4">
                        <div className="space-y-1">
                          {isCamp ? (
                            <>
                              {item.discount_amount && Number(item.discount_amount) > 0 ? (
                                <>
                                  <p className="text-sm text-muted-foreground line-through">Camp: ${Number(item.original_camp_price || 0).toFixed(2)}</p>
                                  <p className="text-sm text-emerald-600 font-medium">{item.discount_label || 'Discount'}: −${Number(item.discount_amount).toFixed(2)}</p>
                                  <p className="text-sm text-muted-foreground">Camp: ${Number(item.camp_price || 0).toFixed(2)}</p>
                                </>
                              ) : (
                                <p className="text-sm text-muted-foreground">Camp: ${Number(item.camp_price || 0).toFixed(2)}</p>
                              )}
                              {item.kit_total > 0 && <p className="text-sm text-primary">Kits: +${Number(item.kit_total).toFixed(2)}</p>}
                              {item.extended_care_cost > 0 && (
                                <p className="text-sm text-purple-600">
                                  Extended Care: +${Number(item.extended_care_cost).toFixed(2)}
                                </p>
                              )}
                            </>
                          ) : (
                            <>
                              <p className="text-sm text-muted-foreground">
                                Course: ${(Number(item.section_price) || Number(item.price) || 0).toFixed(2)}
                              </p>
                              {item.include_kit && item.kit && (
                                <p className="text-sm text-primary">
                                  Kit: +${(Number(item.kit.price) || 0).toFixed(2)}
                                </p>
                              )}
                            </>
                          )}
                          <p className="font-bold text-lg">
                            ${(Number(item.price) || 0).toFixed(2)}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive mt-2"
                          onClick={() => isCamp ? handleRemoveCampItem(item.camp_section_id, item.learner_id, item.guest_student_name) : handleRemoveItem(item.section_id, item.learner_id)}
                          disabled={loading}
                          data-testid={`remove-item-${index}`}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Remove
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                );
              })}

              {/* Shipping Address Form */}
              {needsShipping && (
                <Card className="card-dashboard" data-testid="shipping-form">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Truck className="h-5 w-5" />
                      Shipping Address
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      We'll ship your STEM kit(s) to this address.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          value={shippingAddress.name}
                          onChange={(e) => handleShippingChange('name', e.target.value)}
                          placeholder="John Doe"
                          data-testid="shipping-name"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label htmlFor="address">Street Address *</Label>
                        <Input
                          id="address"
                          value={shippingAddress.address}
                          onChange={(e) => handleShippingChange('address', e.target.value)}
                          placeholder="123 Main St, Apt 4"
                          data-testid="shipping-address"
                        />
                      </div>
                      <div>
                        <Label htmlFor="city">City *</Label>
                        <Input
                          id="city"
                          value={shippingAddress.city}
                          onChange={(e) => handleShippingChange('city', e.target.value)}
                          placeholder="Austin"
                          data-testid="shipping-city"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="state">State *</Label>
                          <Input
                            id="state"
                            value={shippingAddress.state}
                            onChange={(e) => handleShippingChange('state', e.target.value)}
                            placeholder="TX"
                            data-testid="shipping-state"
                          />
                        </div>
                        <div>
                          <Label htmlFor="zip">ZIP Code *</Label>
                          <Input
                            id="zip"
                            value={shippingAddress.zip}
                            onChange={(e) => handleShippingChange('zip', e.target.value)}
                            placeholder="78701"
                            data-testid="shipping-zip"
                          />
                        </div>
                      </div>
                      <div className="md:col-span-2">
                        <Label htmlFor="phone">Phone (optional)</Label>
                        <Input
                          id="phone"
                          value={shippingAddress.phone}
                          onChange={(e) => handleShippingChange('phone', e.target.value)}
                          placeholder="(512) 555-1234"
                          data-testid="shipping-phone"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              {/* Guest info — full left-column width so fields aren't squished */}
              {!user && (
                <Card id="guest-info-form" className="card-marketing border-blue-200">
                  <CardHeader>
                    <CardTitle className="text-lg">Your Information</CardTitle>
                    <p className="text-sm text-muted-foreground">Required to complete your order and send your receipt.</p>
                  </CardHeader>
                  <CardContent>
                    {/* Parent and Student side-by-side — has full 2/3-column width here */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      {/* Parent column */}
                      <div className="space-y-3">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Parent / Guardian</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>First Name *</Label>
                            <Input value={guestInfo.parentFirstName} onChange={e => setGuestField('parentFirstName', e.target.value)} placeholder="Jane" data-testid="guest-parent-first" />
                          </div>
                          <div>
                            <Label>Last Name *</Label>
                            <Input value={guestInfo.parentLastName} onChange={e => setGuestField('parentLastName', e.target.value)} placeholder="Smith" data-testid="guest-parent-last" />
                          </div>
                        </div>
                        <div>
                          <Label>Email *</Label>
                          <Input type="email" value={guestInfo.parentEmail} onChange={e => setGuestField('parentEmail', e.target.value)} placeholder="you@example.com" data-testid="guest-parent-email" />
                        </div>
                        <div>
                          <Label>Phone *</Label>
                          <Input type="tel" value={guestInfo.parentPhone || ''} onChange={e => setGuestField('parentPhone', e.target.value)} placeholder="(555) 123-4567" data-testid="guest-parent-phone" />
                          <p className="text-xs text-muted-foreground mt-1">We'll send a verification code to this number.</p>
                        </div>
                      </div>
                      {/* Student column */}
                      <div className="space-y-3">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Student</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>First Name *</Label>
                            <Input value={guestInfo.studentFirstName} onChange={e => setGuestField('studentFirstName', e.target.value)} placeholder="Alex" data-testid="guest-student-first" />
                          </div>
                          <div>
                            <Label>Last Name *</Label>
                            <Input value={guestInfo.studentLastName} onChange={e => setGuestField('studentLastName', e.target.value)} placeholder="Smith" data-testid="guest-student-last" />
                          </div>
                        </div>
                        <div>
                          <Label>Grade *</Label>
                          <Select value={guestInfo.studentGrade} onValueChange={v => setGuestField('studentGrade', v)}>
                            <SelectTrigger data-testid="guest-student-grade">
                              <SelectValue placeholder="Select grade" />
                            </SelectTrigger>
                            <SelectContent>
                              {['K','1','2','3','4','5','6','7','8','9','10','11','12'].map(g => (
                                <SelectItem key={g} value={g}>{g === 'K' ? 'Kindergarten' : `Grade ${g}`}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-4">We'll email you a link to create a password and manage your enrollments after payment.</p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right — Order Summary */}
            <div className="lg:col-span-1">
              <Card className="card-marketing sticky top-24">
                <CardHeader>
                  <CardTitle>Order Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Coupon code — single source of truth for cart-wide discount.
                      Replaces the per-camp coupon field that used to live on
                      the Summer Camps add-to-cart modal. */}
                  <div className="border border-dashed border-primary/30 bg-primary/5 rounded-lg p-3" data-testid="cart-coupon-section">
                    <Label className="text-xs font-semibold uppercase tracking-wide flex items-center gap-1.5 mb-2">
                      <Tag className="h-3.5 w-3.5" />
                      Coupon Code
                    </Label>
                    {appliedCoupon ? (
                      <div className="flex items-center justify-between gap-2" data-testid="cart-coupon-applied">
                        <div className="flex items-center gap-1.5 text-sm">
                          <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                          <span className="font-semibold text-emerald-700">{appliedCoupon.code}</span>
                          <span className="text-muted-foreground text-xs">
                            — saving ${appliedCoupon.discount_amount.toFixed(2)}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleRemoveCoupon}
                          disabled={couponApplying}
                          className="h-7 w-7 p-0"
                          data-testid="cart-coupon-clear"
                          aria-label="Remove coupon"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleApplyCoupon();
                            }
                          }}
                          placeholder="Enter code"
                          disabled={couponApplying}
                          className="h-9"
                          data-testid="cart-coupon-input"
                        />
                        <Button
                          onClick={handleApplyCoupon}
                          disabled={couponApplying || !couponCode.trim()}
                          variant="outline"
                          size="sm"
                          className="h-9"
                          data-testid="cart-coupon-apply"
                        >
                          {couponApplying ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            'Apply'
                          )}
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    {totals.courseCount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Courses ({totals.courseCount})</span>
                        <span>${(totals.courseTotal - (totals.campCount > 0 ? cart.items.filter(i => i.type === 'camp').reduce((s, i) => s + Number(i.camp_price || 0), 0) : 0)).toFixed(2)}</span>
                      </div>
                    )}
                    {totals.campCount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Camps ({totals.campCount})</span>
                        <span>${cart.items.filter(i => i.type === 'camp').reduce((s, i) => s + Number(i.original_camp_price || i.camp_price || 0), 0).toFixed(2)}</span>
                      </div>
                    )}
                    {cart.items.some(i => Number(i.discount_amount) > 0) && (
                      <div className="flex justify-between text-sm text-emerald-600 font-medium">
                        <span className="flex items-center gap-1">
                          Discounts
                          {(() => {
                            const codes = [...new Set(cart.items.filter(i => i.coupon_code).map(i => i.coupon_code))];
                            return codes.length ? ` (${codes.join(', ')})` : '';
                          })()}
                        </span>
                        <span>−${cart.items.reduce((s, i) => s + Number(i.discount_amount || 0), 0).toFixed(2)}</span>
                      </div>
                    )}
                    {totals.kitTotal > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Package className="h-3 w-3" /> STEM Kits
                        </span>
                        <span>${totals.kitTotal.toFixed(2)}</span>
                      </div>
                    )}
                    {totals.extendedCareTotal > 0 && (
                      <div className="flex justify-between text-sm text-purple-600 font-medium">
                        <span>Extended Care</span>
                        <span>${totals.extendedCareTotal.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="border-t border-border pt-4">
                    <div className="flex justify-between font-bold text-lg">
                      <span>Total</span>
                      <span>${(cart.total != null ? Number(cart.total) : totals.grandTotal).toFixed(2)}</span>
                    </div>
                  </div>

                  {needsShipping && !isShippingValid() && (
                    <p className="text-sm text-amber-600 flex items-center gap-1">
                      <Truck className="h-4 w-4" />
                      Please complete shipping address
                    </p>
                  )}

                  {Number(cart.total) === 0 && appliedCoupon ? (
                    <Button
                      className="btn-primary w-full"
                      onClick={handleFreeCheckout}
                      disabled={checkingOut || loading}
                      data-testid="checkout-btn"
                    >
                      {checkingOut ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          Complete Free Enrollment
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      className="btn-primary w-full"
                      onClick={handleCheckout}
                      disabled={checkingOut || loading || (needsShipping && !isShippingValid())}
                      data-testid="checkout-btn"
                    >
                      {checkingOut ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Processing...
                        </>
                      ) : (
                        <>
                          Proceed to Checkout
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  )}

                  <Link to="/catalog" className="block">
                    <Button variant="outline" className="w-full" data-testid="continue-shopping-btn">
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Browse Catalogue
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>

      {/* Phone Verification Modal */}
      <PhoneVerificationModal
        open={showPhoneVerifyModal}
        phone={guestInfo.parentPhone}
        onVerified={() => {
          setShowPhoneVerifyModal(false);
          executeCheckout();
        }}
        onClose={() => setShowPhoneVerifyModal(false)}
      />

      {/* Payment Fallback Modal */}
      <PaymentFallbackModal
        open={showFallbackModal}
        onClose={() => setShowFallbackModal(false)}
        cartTotal={cart.total}
        onSuccess={handleFallbackSuccess}
      />
    </div>
  );
}
